import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { SUPPORT } from '../lib/hubCopy'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { MyQuestions, SupportInbox, SupportQueueRow } from '../components/SupportInbox'
import { SupportThread } from '../components/SupportThread'
import { SupportStaffPanel } from '../components/SupportStaffPanel'
import { SavedReplies } from '../components/SavedReplies'
import { SupportTabs } from '../components/AskOneDesk'
import { addComment, loadComments, type WikiComment } from '../lib/wiki'
import {
  addSupportNote,
  draftSupportAnswer,
  loadSupportStaffView,
  requestWikiDraft,
  sendToDevelopers,
  type SupportStaffView,
} from '../lib/supportStaff'
import {
  loadSavedReplies,
  removeSavedReply,
  repliesForLane,
  saveSavedReply,
  type SavedReply,
} from '../lib/supportReplies'
import { awardSupportStars, loadSupportStars, type SupportStars } from '../lib/supportStars'
import { GuideLink } from '../components/guides/GuideLink'
import {
  DESK_VIEW_LABELS,
  closeSupportQuestion,
  handSupportQuestion,
  laneLabel,
  loadHandedToMe,
  loadMySupportQuestions,
  loadSupportInbox,
  loadSupportInboxCounts,
  loadSupportLanes,
  requestSupportTranslation,
  loadSupportThread,
  moveSupportQuestion,
  moveTargets,
  myLanes,
  reopenSupportQuestion,
  sayItAnswered,
  type DeskView,
  type MySupportQuestion,
  type SupportKind,
  type SupportLane,
  type SupportQuestion,
  type SupportThreadHead,
  type SupportTurn,
} from '../lib/support'

/**
 * `#/support` — the desk. HLP-3's queue, rebuilt 26 Sep (118) as a split view,
 * and reworked 11 Oct (174, the Support Desk Rework — David: "it's confusing
 * right now. I can't see how to close out" a ticket).
 *
 * ── THE SHAPE (174) ─────────────────────────────────────────────────────────
 *
 *   left    the queue: three views (Needs reply / Waiting on asker / Closed),
 *           then Handed to you and Your own questions as two more places in
 *           the same column — they were cards stacked under the desk
 *   middle  the thread, with Close / Reopen and Details in its head and
 *           "Reply and close" in its composer
 *   right   the Details TRAY: the staff panel (triage, developers, stars,
 *           notes, routing, Wiki One), beside the thread on a wide screen and
 *           over it below 1240px; its forms take the whole tray one at a time.
 *           Saved replies are managed in the same tray.
 *
 * The conversation is NOT in a tray: reading and replying is the job, and a
 * drawer over a full-width list hides the queue being worked through.
 *
 * After an act that moves the thread out of the view (a reply, a close), the
 * NEXT thread in the view opens and a line says where the last one went, with
 * Undo for a close. Before 174 the row vanished from the list while the
 * thread stayed open on the right, with no word about where it had gone.
 *
 * ── ITS OWN ROUTE, FOR 021'S REASON ─────────────────────────────────────────
 *
 * Answering a private question put to your lane is neither administering
 * people nor moderating a colleague's speech, and 085 gave the three support
 * roles no `content.publish`, no `audience.build` and no `content.moderate`.
 *
 * ── EVERYTHING RENDERABLE IS IN components/ ─────────────────────────────────
 *
 * Importing a route pulls in AppShell and then an SVG the test bundler cannot
 * resolve, so anything defined here is unreachable by the suite. The thread is
 * the SAME component `#/support/:id` renders (rule 11).
 *
 * ── THE SERVER SORTS, FILTERS AND SEARCHES (118, standing rule 1) ───────────
 *
 * The view, lane and search are all parameters to `support_inbox()`, applied
 * before the limit (174: the Closed view is `p_turn => 'done'`). This route
 * never sorts or filters the rows it is handed.
 *
 * ── THE REFUSAL IS RENDERED, NOT SWALLOWED ──────────────────────────────────
 *
 * `support_inbox()` RAISES for a lane you do not hold and for holding none
 * (rule 12), so the error goes on screen as a sentence.
 */
type RailView = DeskView | 'handed' | 'mine'

function wideScreen(px: number): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.(`(min-width: ${px}px)`).matches
}

/** The tray's open/closed is a per-viewer convenience — browser storage,
 *  wrapped, and the desk works the same without it. */
function readTrayPref(): boolean | null {
  try {
    const v = window.localStorage.getItem('oneconetec.desk.details')
    return v === null ? null : v === 'open'
  } catch {
    return null
  }
}
function writeTrayPref(open: boolean) {
  try {
    window.localStorage.setItem('oneconetec.desk.details', open ? 'open' : 'closed')
  } catch {
    /* a private window: the default is fine */
  }
}

export function Support({ me }: { me: Me }) {
  const t = SUPPORT[useHubLang()]
  const lanes = myLanes(me)
  const navigate = useNavigate()

  const [lane, setLane] = useState<SupportLane | null>(null)
  // 337 (D3). Tickets, feedback or both; the server filters before the limit.
  const [kind, setKind] = useState<SupportKind | null>(null)
  // Needs reply is the default view: it is the only list a holder needs at
  // 08:00, and the other views carry their counts.
  const [view, setView] = useState<RailView>('holder')
  const deskView: DeskView | null = view === 'handed' || view === 'mine' ? null : view
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<SupportQuestion[]>([])
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState<Record<SupportTurn, number> | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [openId, setOpenId] = useState<string | null>(null)
  // Below 820px the desk shows the list OR the thread; this is which.
  const [showThread, setShowThread] = useState(false)
  const [head, setHead] = useState<SupportThreadHead | null>(null)
  const [replies, setReplies] = useState<WikiComment[]>([])
  const [threadError, setThreadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [staff, setStaff] = useState<SupportStaffView | null>(null)
  const [staffError, setStaffError] = useState<string | null>(null)
  const [staffBusy, setStaffBusy] = useState(false)

  /* 174. The Details tray. Open by default where it fits beside the thread
     (1240px+), closed below that, where it opens OVER the thread; a viewer's
     own choice wins on the next visit. `tray` is what it holds. */
  const [detailsOpen, setDetailsOpen] = useState<boolean>(() => readTrayPref() ?? wideScreen(1240))
  const [tray, setTray] = useState<'details' | 'saved'>('details')
  function toggleDetails(open?: boolean) {
    setDetailsOpen((cur) => {
      const next = open ?? !cur
      writeTrayPref(next)
      return next
    })
    setTray('details')
  }

  /* 174. One line after an act that moved the thread, and Undo for a close.
     role="status": said, not shouted. */
  const [notice, setNotice] = useState<{ text: string; undoId?: string } | null>(null)
  useEffect(() => {
    if (!notice) return
    const h = setTimeout(() => setNotice(null), 6000)
    return () => clearTimeout(h)
  }, [notice])

  /* HLP-22. Your own, which everybody has a claim to — including the ~930
   * people who hold no lane. Its own read and its own state: one failing must
   * not blank the other. */
  const [myRows, setMyRows] = useState<MySupportQuestion[]>([])
  const [myTotal, setMyTotal] = useState(0)
  /* 119. Handed to me — anybody can have one, lane or no lane. */
  const [handedRows, setHandedRows] = useState<SupportQuestion[]>([])
  const [handedTotal, setHandedTotal] = useState(0)
  /* 121. The OPEN lanes, for "move to another lane". `undefined` until the
   * server answers, which `moveTargets()` treats as nowhere. */
  const [openLanes, setOpenLanes] = useState<SupportLane[] | undefined>(undefined)
  /* 122. The saved replies of every lane I hold, in one read. Its own state:
   * `support_saved_replies` REFUSES a person holding no lane. */
  const [saved, setSaved] = useState<SavedReply[]>([])
  const [savedError, setSavedError] = useState<string | null>(null)
  const [savedBusy, setSavedBusy] = useState(false)
  useEffect(() => {
    if (lanes.length === 0) return
    let live = true
    loadSavedReplies(null)
      .then((rows) => live && (setSaved(rows), setSavedError(null)))
      .catch((e: Error) => live && setSavedError(e.message))
    return () => {
      live = false
    }
  }, [lanes.length])
  async function savedAct(act: () => Promise<unknown>) {
    setSavedBusy(true)
    setSavedError(null)
    try {
      await act()
      setSaved(await loadSavedReplies(null))
    } catch (e) {
      setSavedError(messageOf(e))
    } finally {
      setSavedBusy(false)
    }
  }

  function loadMine() {
    loadMySupportQuestions()
      .then((page) => {
        setMyRows(page.rows)
        setMyTotal(page.totalMatches)
      })
      // Deliberately silent: an empty "your questions" is a TRUE statement
      // about you. The inbox's refusal is the opposite case and IS rendered.
      .catch(() => undefined)
    loadHandedToMe()
      .then((page) => {
        setHandedRows(page.rows)
        setHandedTotal(page.totalMatches)
      })
      .catch(() => undefined)
  }
  useEffect(() => {
    let live = true
    loadMine()
    loadSupportLanes()
      .then((rows) => live && setOpenLanes(rows.map((r) => r.lane)))
      // A failed read leaves it undefined: no move door, rather than a wrong one.
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  // The search is debounced so a keystroke is not a round trip; the SERVER
  // still does the matching.
  const [searchSent, setSearchSent] = useState('')
  useEffect(() => {
    const h = setTimeout(() => setSearchSent(search.trim()), 250)
    return () => clearTimeout(h)
  }, [search])

  /** Re-read the view. Returns the page so an act can pick the next thread. */
  async function refreshQueue(): Promise<SupportQuestion[] | null> {
    if (!deskView) {
      loadMine()
      return null
    }
    try {
      const [page, c] = await Promise.all([
        loadSupportInbox({ lane, turn: deskView, search: searchSent, kind }),
        loadSupportInboxCounts(lane, kind),
      ])
      setRows(page.rows)
      setTotal(page.totalMatches)
      setCounts(c)
      setError(null)
      return page.rows
    } catch (e) {
      setError(messageOf(e))
      return null
    }
  }

  useEffect(() => {
    if (lanes.length === 0 || !deskView) return
    let live = true
    Promise.all([
      loadSupportInbox({ lane, turn: deskView, search: searchSent, kind }),
      loadSupportInboxCounts(lane, kind),
    ])
      .then(([page, c]) => {
        if (!live) return
        setRows(page.rows)
        setTotal(page.totalMatches)
        setCounts(c)
        setError(null)
        // On a wide screen the first row opens itself, so the desk never shows
        // an empty middle beside a full list. On a phone nothing opens until
        // it is pressed — the list IS the screen there.
        setOpenId((cur) => {
          if (cur && page.rows.some((r) => r.questionId === cur)) return cur
          return wideScreen(820) ? page.rows[0]?.questionId ?? null : null
        })
      })
      .catch((e: Error) => live && setError(e.message))
    return () => {
      live = false
    }
    // `lanes.length` rather than `lanes`: myLanes() builds a new array every
    // render, so depending on the array itself would re-fetch on every one.
  }, [lane, kind, deskView, searchSent, lanes.length])

  // 119: a holder of THIS lane, or the one person it was handed to. `holder`
  // is the narrower — handing on and sending to developers stay the lane's.
  const holder = !!head && lanes.includes(head.lane)
  const isStaff = holder || (!!head && head.iAmAssignee)

  async function loadThread(id: string) {
    const [h, c] = await Promise.all([loadSupportThread(id), loadComments(id)])
    setHead(h)
    setReplies(c)
    setThreadError(null)
  }

  useEffect(() => {
    if (!openId) {
      setHead(null)
      setReplies([])
      setStaff(null)
      return
    }
    let live = true
    setThreadError(null)
    loadThread(openId).catch((e: Error) => live && setThreadError(e.message))
    // 123. Ask for any rendering this thread is still missing in a reader's
    // language; re-read only if something was stored. Idempotent and cheap.
    void requestSupportTranslation(openId).then((stored) => {
      if (live && stored.length > 0) loadThread(openId).catch(() => undefined)
    })
    return () => {
      live = false
    }
  }, [openId])

  // 117. The staff panel, only when the session staffs this question — a
  // presentation convenience; `support_staff_view()` refuses anybody else.
  useEffect(() => {
    if (!openId || !isStaff) return
    let live = true
    loadSupportStaffView(openId)
      .then((v) => live && (setStaff(v), setStaffError(null)))
      .catch((e: Error) => live && setStaffError(e.message))
    return () => {
      live = false
    }
  }, [openId, isStaff])

  // 172. Stars on a platform ticket, for whoever staffs it. Before 174 the
  // desk never loaded them, so the picker existed only on #/support/:id.
  const [stars, setStars] = useState<SupportStars | null>(null)
  const starsFor = !!openId && !!head && head.lane === 'platform' && isStaff
  useEffect(() => {
    if (!openId || !starsFor) {
      setStars(null)
      return
    }
    let live = true
    loadSupportStars(openId)
      .then((s) => live && setStars(s))
      .catch(() => live && setStars(null))
    return () => {
      live = false
    }
  }, [openId, starsFor])

  /**
   * 174. After an act, if the thread has LEFT the view, open the next one in
   * the order the server gave (the row that took its place), and say where it
   * went. If it is still in the view, it stays open.
   */
  async function afterAct(id: string, before: SupportQuestion[], said: string, undo?: boolean) {
    const page = await refreshQueue()
    if (page && !page.some((r) => r.questionId === id)) {
      const at = Math.max(0, before.findIndex((r) => r.questionId === id))
      const next = page[Math.min(at, page.length - 1)]
      setOpenId(next?.questionId ?? null)
      if (!next) setShowThread(false)
    } else if (openId === id) {
      await loadThread(id)
    }
    setNotice({ text: said, undoId: undo ? id : undefined })
  }

  /**
   * The reply. `addComment` and nothing else — 035: replying to a post and
   * replying to a question are the same act. The thread AND the queue are
   * re-read rather than spliced (Moderation.tsx's reason).
   */
  async function reply(body: string, andClose = false) {
    if (!openId) return
    const id = openId
    const firstName = (head?.askedBy ?? t.somebody).split(/\s+/)[0]
    const before = rows
    setBusy(true)
    setThreadError(null)
    try {
      await addComment(openId, body)
      // 123. So the asker's rendering is ready before they open it. Not
      // awaited: the holder does not need to see their own words translated.
      void requestSupportTranslation(openId)
      // 174. Reply and close: the close is a second act, after the reply, so
      // the server's close time is the later one and the thread reads closed.
      if (andClose) await closeSupportQuestion(id)
      await afterAct(
        id,
        before,
        andClose ? `Replied and closed. ${firstName} was asked to confirm.` : `Replied. ${firstName} has it now.`,
        andClose,
      )
    } catch (e) {
      setThreadError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  /** 174. The lane closes it; the asker is asked to confirm. */
  async function close() {
    if (!openId) return
    const id = openId
    const firstName = (head?.askedBy ?? t.somebody).split(/\s+/)[0]
    const before = rows
    setBusy(true)
    setThreadError(null)
    try {
      await closeSupportQuestion(id)
      await afterAct(id, before, `Closed. ${firstName} was asked to confirm.`, true)
    } catch (e) {
      setThreadError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  /** 174. Reopen, from the thread or from the line's Undo. */
  async function reopen(id: string, fromUndo = false) {
    setBusy(true)
    setThreadError(null)
    try {
      await reopenSupportQuestion(id)
      await refreshQueue()
      setOpenId(id)
      setShowThread(true)
      if (openId === id) await loadThread(id)
      setNotice({ text: fromUndo ? 'Undone. It is open again.' : 'Reopened.' })
    } catch (e) {
      setThreadError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  async function verdict(answered: boolean) {
    if (!openId) return
    setBusy(true)
    setThreadError(null)
    try {
      await sayItAnswered(openId, answered)
      await loadThread(openId)
      await refreshQueue()
    } catch (e) {
      setThreadError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  async function staffAct(act: () => Promise<void>) {
    if (!openId) return
    setStaffBusy(true)
    setStaffError(null)
    try {
      await act()
      // 123. A note needs rendering for a colleague in another language; a
      // hand (119) or a move (121) may have added such a reader. Not awaited.
      void requestSupportTranslation(openId)
      // Re-read rather than splice: the server may have refused half of it.
      setStaff(await loadSupportStaffView(openId))
      await loadThread(openId)
      await refreshQueue()
    } catch (e) {
      setStaffError(messageOf(e))
    } finally {
      setStaffBusy(false)
    }
  }

  /**
   * 121. After a move this lane may no longer be able to read the thread, so
   * the desk CLOSES it and re-reads the queue, rather than re-reading a
   * thread that will refuse.
   */
  async function move(lane: SupportLane, reason: string) {
    if (!openId) return
    setStaffBusy(true)
    setStaffError(null)
    try {
      await moveSupportQuestion(openId, lane, reason)
      setOpenId(null)
      setShowThread(false)
      await refreshQueue()
      setNotice({ text: `Moved to ${laneLabel(lane)}.` })
    } catch (e) {
      setStaffError(messageOf(e))
    } finally {
      setStaffBusy(false)
    }
  }

  function open(id: string) {
    setOpenId(id)
    setShowThread(true)
  }

  function pickView(next: RailView) {
    setView(next)
    setNotice(null)
    if (next === 'handed' || next === 'mine') {
      // These two lists are not the lane's queue; nothing opens by itself.
      setOpenId(null)
      setShowThread(false)
      loadMine()
    }
  }

  /* ── KEYS, for the person who lives here ──────────────────────────────────
   * j / k move, Enter opens on a phone, r focuses the reply, n the note mode,
   * e closes (174), / the search. Nothing fires while typing. */
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const openRef = useRef(openId)
  openRef.current = openId
  const closeRef = useRef<() => void>(() => undefined)
  closeRef.current = () => {
    if (head && isStaff && !busy && head.turn !== 'closed' && head.turn !== 'answered') void close()
  }
  useEffect(() => {
    if (lanes.length === 0) return
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase()
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || (e.target as HTMLElement | null)?.isContentEditable
      if (typing) {
        if (e.key === 'Escape') (e.target as HTMLElement).blur()
        return
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const list = rowsRef.current
      const i = list.findIndex((r) => r.questionId === openRef.current)
      if (e.key === 'j' && list.length) {
        setOpenId(list[Math.min(list.length - 1, i + 1)].questionId)
      } else if (e.key === 'k' && list.length) {
        setOpenId(list[Math.max(0, i - 1)].questionId)
      } else if (e.key === 'Enter' && openRef.current) {
        setShowThread(true)
      } else if (e.key === 'e') {
        e.preventDefault()
        closeRef.current()
      } else if (e.key === 'r' || e.key === 'n') {
        e.preventDefault()
        if (e.key === 'n') {
          // The mode chip carries aria-pressed; pressing it is the same act.
          const chip = document.querySelector<HTMLButtonElement>('.supportq__modes .chip:nth-child(2)')
          chip?.click()
        } else {
          document.querySelector<HTMLButtonElement>('.supportq__modes .chip:nth-child(1)')?.click()
        }
        document.querySelector<HTMLTextAreaElement>('.supportq__answer textarea')?.focus()
      } else if (e.key === '/') {
        e.preventDefault()
        document.getElementById('queue-search')?.focus()
      } else if (e.key === 'Escape') {
        setDetailsOpen((cur) => (cur && !wideScreen(1240) ? false : cur))
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [lanes.length])

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Support inbox"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={lanes}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {/* 126. The two sections of #/support, for analytics.read only — a lane
          holder has one section, and one tab is decoration. */}
      {can(me, 'analytics.read') && <SupportTabs on="desk" deskLabel={lanes.length ? 'Desk' : 'Your questions'} />}
      {children}
    </AppShell>
  )

  /* ── HLP-22: THIS SCREEN HAS TWO SIDES AND MOST PEOPLE ONLY HAVE ONE ───────
   * Your own questions (everybody), the desk (holders). Somebody holding no
   * lane sees what they always did: Handed to you (when there is any) and
   * Your questions, as cards. */
  if (lanes.length === 0) {
    return shell(
      <>
        {handedRows.length > 0 && (
          <Card title={t.handedToYou}>
            <p className="supportinbox__lede">{t.handedToYouLede}</p>
            <ul className="queue__list">
              {handedRows.map((q) => (
                <SupportQueueRow key={q.questionId} question={q} onOpen={() => navigate(`/support/${q.questionId}`)} />
              ))}
            </ul>
            {handedTotal > handedRows.length && (
              <p className="supportinbox__page">Showing {handedRows.length} of {handedTotal}.</p>
            )}
          </Card>
        )}
        <Card title={t.yourQuestions}>
          <p className="supportinbox__lede">{t.yourQuestionsLede}</p>
          <MyQuestions rows={myRows} totalMatches={myTotal} onOpen={(id) => navigate(`/support/${id}`)} />
        </Card>
      </>,
    )
  }

  const needs = counts?.holder ?? null
  const trayShown = detailsOpen && ((!!head && isStaff && !!staff) || tray === 'saved')

  /* 122. The lane's standard answers — the list and the form, in the tray. */
  const savedCard = (
    <Card title="Saved replies">
      <GuideLink guide="support-desk" section="saved" label="How saved replies work" />
      <SavedReplies
        lanes={lanes}
        rows={saved}
        onSave={(id, l, ti, b) => void savedAct(() => saveSavedReply(id, l, ti, b))}
        onRemove={(id) => void savedAct(() => removeSavedReply(id))}
        busy={savedBusy}
        error={savedError}
      />
    </Card>
  )

  return shell(
    <section
      className={`desk${showThread ? ' desk--thread' : ''}${trayShown ? ' desk--tray' : ''}`}
      aria-label="Support inbox"
    >
      <div className="desk__queue">
        <div className="desk__head">
          {/* The page title already says what this is; a lane filter is news,
              so it is named. */}
          {lane && <h2 className="desk__title">{laneLabel(lane)}</h2>}
          {/* What needs YOUR reply, for the whole lane — the one number the
              heading carries. Per turn, never per person (B22, rule 8). */}
          {needs !== null && (
            <span className={`pill${needs > 0 ? ' pill--live' : ' pill--kind'}`}>
              {needs === 0 ? 'Nothing needs a reply' : `${needs} ${needs === 1 ? 'needs' : 'need'} a reply`}
            </span>
          )}
        </div>
        <p className="supportinbox__lede">
          Private questions put to a lane you answer. Nobody else can read them.
        </p>
        <p className="desk__guides">
          <GuideLink guide="support-desk" section="queue" label="How the queue works" />
          {/* Feedback is a desk kind (D3): its own link beside the queue's. */}
          <GuideLink guide="support-desk" section="feedback" label="How feedback works" />
        </p>
        {error && (
          <p className="roles__error" role="alert">
            {error}
          </p>
        )}
        {deskView ? (
          <SupportInbox
            lanes={lanes}
            lane={lane}
            onLane={(next) => setLane(next)}
            kind={kind}
            onKind={setKind}
            view={deskView}
            onView={(next) => pickView(next)}
            counts={counts}
            search={search}
            onSearch={setSearch}
            rows={rows}
            totalMatches={total}
            openQuestionId={openId}
            onOpen={open}
          />
        ) : (
          <div className="supportinbox">
            <button type="button" className="btn btn--quiet btn--sm desk__backviews" onClick={() => pickView('holder')}>
              ← {DESK_VIEW_LABELS.holder}
            </button>
            {view === 'handed' ? (
              <>
                <h2 className="desk__title">{t.handedToYou}</h2>
                <p className="supportinbox__lede">{t.handedToYouLede}</p>
                {handedRows.length === 0 ? (
                  <p className="queue__empty">Nothing has been handed to you.</p>
                ) : (
                  <ul className="queue__list">
                    {handedRows.map((q) => (
                      <SupportQueueRow key={q.questionId} question={q} current={openId === q.questionId} onOpen={() => open(q.questionId)} />
                    ))}
                  </ul>
                )}
                {handedTotal > handedRows.length && (
                  <p className="supportinbox__page">Showing {handedRows.length} of {handedTotal}.</p>
                )}
              </>
            ) : (
              <>
                <h2 className="desk__title">{t.yourQuestions}</h2>
                <p className="supportinbox__lede">{t.yourQuestionsLede}</p>
                <MyQuestions rows={myRows} totalMatches={myTotal} onOpen={open} />
              </>
            )}
          </div>
        )}
        {/* 174. The other places a thread can be for you, and the lane's
            saved replies — in the queue column, not stacked under the desk. */}
        <nav className="desk__more" aria-label="More">
          <button type="button" className={`desk__morelink${view === 'handed' ? ' desk__morelink--on' : ''}`} aria-pressed={view === 'handed'} onClick={() => pickView('handed')}>
            {t.handedToYou} <span className="deskview__n">{handedTotal}</span>
          </button>
          <button type="button" className={`desk__morelink${view === 'mine' ? ' desk__morelink--on' : ''}`} aria-pressed={view === 'mine'} onClick={() => pickView('mine')}>
            {t.yourQuestions} <span className="deskview__n">{myTotal}</span>
          </button>
          <button
            type="button"
            className={`desk__morelink${detailsOpen && tray === 'saved' ? ' desk__morelink--on' : ''}`}
            aria-pressed={detailsOpen && tray === 'saved'}
            onClick={() => {
              setTray('saved')
              setDetailsOpen(true)
            }}
          >
            Manage saved replies <span className="deskview__n">{saved.length}</span>
          </button>
        </nav>
      </div>

      <div className="desk__reading">
        {notice && (
          <p className="desk__notice" role="status">
            <span>{notice.text}</span>
            {notice.undoId && (
              <button type="button" className="btn btn--quiet btn--xs" disabled={busy} onClick={() => void reopen(notice.undoId!, true)}>
                Undo
              </button>
            )}
          </p>
        )}
        {!openId && (
          <p className="desk__none">
            {deskView && rows.length === 0 ? `Nothing in ${DESK_VIEW_LABELS[deskView].toLowerCase()}.` : 'Pick a question.'}
          </p>
        )}
        {openId && threadError && (
          <p className="roles__error" role="alert">{threadError}</p>
        )}
        {openId && !head && !threadError && <p className="desk__none">{t.opening}</p>}
        {head && (
          <SupportThread
            key={head.questionId}
            head={head}
            replies={replies}
            onReply={(body) => void reply(body)}
            onNote={isStaff ? (b) => void staffAct(() => addSupportNote(head.questionId, b)) : undefined}
            // 122. The lane's saved replies, for a HOLDER of this lane only.
            savedReplies={holder ? repliesForLane(saved, head.lane) : []}
            // The verdict is the ASKER's (rule 21). A holder who also asked
            // this one gets it, and the server refuses everybody else.
            onVerdict={head.iAsked ? (a) => void verdict(a) : undefined}
            // 174. The lane's close — whoever staffs it; the server checks.
            onClose={isStaff ? () => void close() : undefined}
            onReopen={isStaff ? () => void reopen(head.questionId) : undefined}
            onReplyAndClose={isStaff ? (body) => void reply(body, true) : undefined}
            detailsOpen={detailsOpen && tray === 'details'}
            onToggleDetails={isStaff ? () => toggleDetails(!(detailsOpen && tray === 'details')) : undefined}
            // Only the phone layout needs a way back to the list; the CSS
            // hides this button beside a visible queue.
            onBack={() => setShowThread(false)}
            busy={busy || staffBusy}
          />
        )}
        {isStaff && !staff && staffError && (
          <p className="roles__error" role="alert">{staffError}</p>
        )}
      </div>

      {/* ── THE TRAY (174) ─────────────────────────────────────────────────
          Beside the thread from 1240px, over it below. Holds the staff panel
          or the saved-reply manager, never both. */}
      {trayShown && (
        <aside className="desk__tray" aria-label={tray === 'saved' ? 'Saved replies' : t.details}>
          {tray === 'saved' ? (
            <>
              <div className="desk__trayhead">
                <button type="button" className="btn btn--quiet btn--sm" onClick={() => toggleDetails(false)} aria-label="Close saved replies">
                  ✕
                </button>
              </div>
              {savedCard}
            </>
          ) : (
            head && staff && (
              <SupportStaffPanel
                key={`${staff.handoff?.requestedAt ?? 'none'}-${staff.handoff?.trackerIssueId ?? ''}-${head.assignedToPersonId ?? ''}-${staff.wikiDraft?.draftedAt ?? ''}-${staff.wikiAnswer?.state ?? ''}`}
                view={staff}
                onSend={holder ? (t2, b) => void staffAct(() => sendToDevelopers(head.questionId, t2, b)) : undefined}
                handed={{ to: head.assignedTo, at: head.assignedAt, iAmAssignee: head.iAmAssignee }}
                onHand={(p, n) => void staffAct(() => handSupportQuestion(head.questionId, p, n))}
                canHandOn={holder}
                // 120. Anybody staffing it may ask for the machine's draft; only a HOLDER
                // confirms it into Wiki One (the server refuses the assignee).
                onWikiDraft={() => void staffAct(async () => { await requestWikiDraft(head.questionId) })}
                onWikiConfirm={holder ? (q, a) => void staffAct(async () => { await draftSupportAnswer(head.questionId, q, a) }) : undefined}
                // 121. Moving lanes is the HOLDER's (the server refuses the assignee).
                moveTargets={holder ? moveTargets(head.lane, openLanes) : []}
                onMove={holder ? (l, r) => void move(l, r) : undefined}
                // 172. Stars: the HOLDER awards (the server refuses the assignee and the asker).
                stars={stars}
                onAward={holder ? (lvl) => void staffAct(async () => {
                  await awardSupportStars(head.questionId, lvl)
                  setStars(await loadSupportStars(head.questionId))
                }) : undefined}
                onDismiss={() => toggleDetails(false)}
                busy={staffBusy}
                error={staffError}
              />
            )
          )}
        </aside>
      )}
      {trayShown && <button type="button" className="desk__scrim" aria-label="Close details" onClick={() => toggleDetails(false)} />}
    </section>,
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}
